import * as React from 'react';
import { PropsWithChildren } from 'react';
import { AppContext, AppContextType } from '../../AppContextType';
import { Attribute } from '../../Model/Attribute';
import { Product, ProductInterface } from '../../Model/Product';
import { ObjectUtility } from '../../Service/ObjectUtility';
import { TitleService } from '../../Service/TitleService';
import { UriBuilder } from '../../Service/UriBuilder';
import { Trans } from '../Trans';
import { AttributeView } from './AttributeView';

export interface AttributeCellProps {
    product: ProductInterface
    attr: string
    hideEmpty?: boolean
    extra?: boolean
    colSpan?: number
    dontLink?: boolean
    className?: string
    suffix?: string
}

export abstract class AbstractAttributeCell<P extends PropsWithChildren<AttributeCellProps>> extends React.PureComponent<PropsWithChildren<P>> {
    public static contextType = AppContextType;
    public context: AppContext<Product> = {} as AppContext<Product>;

    public render() {
        const props = this.props;
        if (props.extra && !this.context.handler.showExtraColumns) {
            return null;
        }

        const className = (props.className || '') + (props.extra ? ' extra' : '');
        const attr = props.attr;

        const content = this.renderInner();
        const title = process.env.NODE_ENV === 'development' ? attr : undefined;
        if (content || !props.hideEmpty) {
            return <td className={className} title={title} colSpan={props.colSpan}>
                {content}
            </td>;
        } else {
            return null;
        }
    }

    protected abstract renderInner(): JSX.Element | null | undefined;

    protected getTitle(title: string | undefined, attr: string): string | undefined {
        if (title !== undefined) {
            return title;
        }

        const titleService: TitleService = this.context.serviceLocator.get('titleService');

        return titleService.detectTitle(attr);
    }

    /**
     * Build the cell content
     *
     * If no children are given the content will be auto-generated for `attr`
     * If children is a function it will be invoked (this allows to perform have calculations only if the cell is rendered)
     * Otherwise the children will be returned
     *
     * @param {*} props
     * @param {ProductInterface} product
     * @param {string} attr
     * @param {string | undefined} title
     * @return {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | React.ReactNodeArray | React.ReactPortal | boolean | any}
     */
    protected buildContent(props: P, product: ProductInterface, attr: string, title: string | undefined) {
        const children = props.children;
        if (typeof children === 'undefined') {
            return this.autoBuildContent(product, attr, title, props.suffix);
        } else if (typeof children === 'function') {
            return children(props);
        }
        return children;
    }

    protected autoBuildContent(
        product: ProductInterface,
        attr: string,
        title: string | undefined,
        suffix: string | undefined
    ) {
        const value = ObjectUtility.valueForKeyPath(product, attr);
        const titleTag = title ? <h4 className="pf-cell-title"><Trans>{title}</Trans></h4> : undefined;

        if (value instanceof Attribute) {
            return <>
                {titleTag}
                <AttributeView attribute={value as Attribute} suffix={suffix}/>
            </>;
        } else {
            const suffixTranslated = suffix ? this.context.i18n.translate(suffix) : '';

            return <>
                {titleTag}
                <div>{value}{suffixTranslated}</div>
            </>;
        }
    }

    protected link(content: any): JSX.Element {
        const props = this.props;
        if (props.dontLink) {
            return content;
        }
        const product = props.product;
        const uri = UriBuilder.productUri(product);
        if (!uri) {
            return content;
        }

        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line react/jsx-no-target-blank
            return <a href={uri} target="_blank">{content}</a>;
        } else {
            return <a href={uri}>{content}</a>;
        }
    }
}
